import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useTenant } from '../../context/TenantContext';

const CONFIG_RESULTADO = {
  aprovado: {
    rotulo: 'Aprovado',
    classe: 'bg-emerald-900/40 text-emerald-300 border border-emerald-800/50',
    dot: 'bg-emerald-400',
  },
  reprovado: {
    rotulo: 'Reprovado',
    classe: 'bg-red-900/40 text-red-300 border border-red-800/50',
    dot: 'bg-red-400',
  },
  aprovado_com_restricao: {
    rotulo: 'Ap. c/ Restrição',
    classe: 'bg-amber-900/40 text-amber-300 border border-amber-800/50',
    dot: 'bg-amber-400',
  },
};

const CONFIG_TIPO = {
  externa: { rotulo: 'Externa', icone: '🏭', classe: 'text-blue-400' },
  interna: { rotulo: 'Interna', icone: '🔧', classe: 'text-indigo-400' },
};

export default function CalibrationTimeline({ instrumentId, onNovaCalibracao }) {
  const { formatarData } = useTenant();
  const [calibracoes, setCalibracoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [expandido, setExpandido] = useState(null);

  const buscarCalibracoes = useCallback(async () => {
    if (!instrumentId) return;
    setCarregando(true);
    setErro('');
    try {
      const { data } = await api.get(`/calibrations?instrumentId=${instrumentId}&limite=50`);
      setCalibracoes(data.dados || data);
    } catch {
      setErro('Não foi possível carregar o histórico de calibrações.');
    } finally {
      setCarregando(false);
    }
  }, [instrumentId]);

  useEffect(() => {
    buscarCalibracoes();
  }, [buscarCalibracoes]);

  function toggleExpandir(id) {
    setExpandido((prev) => (prev === id ? null : id));
  }

  if (carregando) {
    return (
      <div className="space-y-3 mt-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-[var(--surface-4)] rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (erro) {
    return (
      <div className="mt-4 bg-red-900/30 border border-red-800/50 text-red-300 rounded-lg px-4 py-3 text-sm">
        {erro}
      </div>
    );
  }

  if (calibracoes.length === 0) {
    return (
      <div className="mt-4 flex flex-col items-center justify-center py-10 text-slate-500">
        <span className="text-3xl mb-2">📋</span>
        <p className="text-sm">Nenhuma calibração registrada para este instrumento.</p>
        {onNovaCalibracao && (
          <button
            onClick={onNovaCalibracao}
            className="mt-3 text-blue-400 hover:text-blue-300 text-sm underline underline-offset-2"
          >
            Registrar primeira calibração
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 relative">
      <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[var(--surface-4)]" />
      <div className="space-y-3">
        {calibracoes.map((cal, idx) => {
          const cfgResultado = CONFIG_RESULTADO[cal.resultado] || {};
          const cfgTipo = CONFIG_TIPO[cal.calibrationType] || CONFIG_TIPO.externa;
          const estaExpandido = expandido === cal._id;
          const ehPrimeiro = idx === 0;

          return (
            <div key={cal._id} className="relative pl-8">
              <div className={`absolute left-0 top-4 w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center ${ehPrimeiro ? 'border-blue-500 bg-[var(--surface-3)]' : 'border-[var(--surface-4)] bg-[var(--surface-1)]'}`}>
                <div className={`w-2 h-2 rounded-full ${cfgResultado.dot || 'bg-slate-500'}`} />
              </div>

              <div className={`bg-[var(--surface-3)] border rounded-lg overflow-hidden transition-all ${ehPrimeiro ? 'border-blue-800/50' : 'border-[var(--surface-4)]'}`}>
                <button
                  onClick={() => toggleExpandir(cal._id)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[var(--surface-4)]/40 transition-colors"
                >
                  <div className="min-w-[90px]">
                    <div className="text-xs text-slate-400">{new Date(cal.data).toLocaleDateString('pt-BR')}</div>
                    {ehPrimeiro && <div className="text-[10px] text-blue-400 font-medium">Mais recente</div>}
                  </div>

                  <div className={`text-xs font-medium ${cfgTipo.classe} min-w-[80px]`}>
                    {cfgTipo.icone} {cfgTipo.rotulo}
                  </div>

                  <div className="flex-1">
                    {cal.resultado && (
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${cfgResultado.classe}`}>
                        {cfgResultado.rotulo}
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-slate-500 max-w-[140px] truncate hidden sm:block">
                    {cal.calibrationType === 'interna'
                      ? <span className="italic">Laboratório interno</span>
                      : cal.fornecedor || '—'}
                  </div>

                  <div className="text-xs text-right min-w-[90px] hidden md:block">
                    <div className="text-slate-500 text-[10px]">Próxima</div>
                    <div className={proximaDataClasse(cal.proximaData)}>
                      {formatarData(cal.proximaData)}
                    </div>
                  </div>

                  <div className={`text-slate-500 text-xs transition-transform ${estaExpandido ? 'rotate-180' : ''}`}>▼</div>
                </button>

                {estaExpandido && (
                  <div className="border-t border-[var(--surface-4)] px-4 py-4 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        ['Nº Certificado', cal.numeroCertificado || '—'],
                        ['Acreditação', formatarAcreditacao(cal.acreditacaoFornecedor)],
                        ['Status do Laudo', cal.statusLaudo === 'concluido' ? '✅ Concluído' : '📝 Rascunho'],
                        ['Registrado em', new Date(cal.createdAt).toLocaleDateString('pt-BR')],
                      ].map(([label, valor]) => (
                        <div key={label}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
                          <div className="text-sm text-slate-300">{valor}</div>
                        </div>
                      ))}
                    </div>

                    {cal.padraoRastreabilidade?.descricao && (
                      <div className="bg-[var(--surface-1)] rounded-lg px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Padrão de Rastreabilidade</div>
                        <div className="text-sm text-slate-300">{cal.padraoRastreabilidade.descricao}</div>
                        {cal.padraoRastreabilidade.numeroCertificado && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            Cert: {cal.padraoRastreabilidade.numeroCertificado}
                            {cal.padraoRastreabilidade.validoAte && (
                              <> · Válido até {new Date(cal.padraoRastreabilidade.validoAte).toLocaleDateString('pt-BR')}</>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {cal.gruposMedicao?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Medições</div>
                        <div className="space-y-3">
                          {cal.gruposMedicao.map((grupo, gi) => (
                            <div key={gi} className="bg-[var(--surface-1)] rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-slate-200">{grupo.nome}</span>
                                <span className="text-xs text-slate-500">({grupo.unidade})</span>
                                <span className="ml-auto text-xs bg-[var(--surface-3)] text-slate-400 px-2 py-0.5 rounded">
                                  EMP: {grupo.empValor} {grupo.empTipo === 'percentage' ? '%' : grupo.unidade}
                                </span>
                              </div>
                              {grupo.pontos?.length > 0 && (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-slate-600 border-b border-[var(--surface-3)]">
                                      <th className="text-left py-1">Aplicado</th>
                                      <th className="text-left py-1">Lido</th>
                                      <th className="text-left py-1">Erro</th>
                                      <th className="text-left py-1">Incerteza</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {grupo.pontos.map((p, pi) => (
                                      <tr key={pi} className="border-b border-[var(--surface-3)]/50 last:border-0">
                                        <td className="py-1 text-slate-300">{p.aplicado}</td>
                                        <td className="py-1 text-slate-300">{p.lido}</td>
                                        <td className={`py-1 font-mono font-medium ${
                                          p.erro == null ? 'text-slate-500' :
                                          Math.abs(p.erro) <= parseFloat(grupo.empValor) ? 'text-emerald-400' : 'text-red-400'
                                        }`}>
                                          {p.erro != null ? p.erro.toFixed(4) : '—'}
                                        </td>
                                        <td className="py-1 text-slate-500">
                                          {p.incerteza != null ? `U=${p.incerteza}` : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {cal.observacoes && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Observações</div>
                        <p className="text-sm text-slate-400 bg-[var(--surface-1)] rounded-lg px-3 py-2">{cal.observacoes}</p>
                      </div>
                    )}

                    {cal.calibrationType === 'interna' && cal.statusLaudo === 'concluido' && (
                      <div className="flex justify-end pt-1">
                        <button
                          onClick={async () => {
                            try {
                              const res = await api.get(`/calibrations/${cal._id}/certificado`, { responseType: 'blob' });
                              const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `certificado_${cal.instrument?.tag || cal._id}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch { alert('Erro ao gerar certificado.'); }
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-900/30 border border-blue-700/50 text-blue-300 hover:bg-blue-900/50 rounded-lg text-sm transition-colors"
                        >
                          ⬇ Certificado PDF
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function proximaDataClasse(dataStr) {
  if (!dataStr) return 'text-slate-400';
  const dias = Math.ceil((new Date(dataStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (dias < 0) return 'text-red-400 font-medium';
  if (dias <= 30) return 'text-amber-400 font-medium';
  return 'text-slate-300';
}

function formatarAcreditacao(valor) {
  const mapa = { RBC: 'RBC', nao_RBC: 'Não-RBC', interno: 'Interno' };
  return mapa[valor] || '—';
}